(() => {
    const robotsName = /^(robots|googlebot(?:-news|-image|-video)?|bingbot|slurp|duckduckbot|baiduspider|yandex(?:bot)?)$/i;
    const socialProperties = new Set(['og:url', 'og:title', 'og:description']);
    const socialNames = new Set(['twitter:title', 'twitter:description']);
    let revision = 0;
    let controller;
    let pendingURL;
    let pendingIntent;
    let committedURL = window.location.href;

    const eligibleMeta = node => robotsName.test(node.getAttribute('name') || '') ||
        socialNames.has((node.getAttribute('name') || '').toLowerCase()) ||
        socialProperties.has((node.getAttribute('property') || '').toLowerCase()) ||
        (node.getAttribute('name') || '').toLowerCase() === 'description';

    const cleanURL = href => {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
            throw new Error('Collection destination changed');
        }
        url.searchParams.delete('section_id');
        url.searchParams.delete('sections');
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview && !url.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', preview);
        url.hash = '';
        return url;
    };

    const readPage = (html, url) => {
        const page = new DOMParser().parseFromString(html, 'text/html');
        const titles = page.head.querySelectorAll('title');
        const canonicals = page.head.querySelectorAll('link[rel="canonical"]');
        const current = document.getElementById('main-collection-product-grid');
        const incoming = page.getElementById('main-collection-product-grid');
        if (titles.length !== 1 || !titles[0].textContent.trim() || canonicals.length !== 1 ||
            !current || !incoming || current.dataset.id !== incoming.dataset.id ||
            !page.querySelector('#CollectionProductGrid .collection')) {
            throw new Error('Incomplete collection page');
        }
        const canonical = new URL(canonicals[0].getAttribute('href'), url);
        if (canonical.origin !== url.origin || canonical.pathname !== url.pathname) {
            throw new Error('Unexpected collection canonical');
        }
        if (page.head.querySelectorAll('meta[name="description"]').length > 1) {
            throw new Error('Ambiguous collection description');
        }
        return page;
    };

    const applyHead = page => {
        document.title = page.head.querySelector('title').textContent;
        document.head.querySelectorAll('link[rel="canonical"]').forEach(node => node.remove());
        document.head.appendChild(document.importNode(page.head.querySelector('link[rel="canonical"]'), true));
        Array.from(document.head.querySelectorAll('meta')).filter(eligibleMeta).forEach(node => node.remove());
        Array.from(page.head.querySelectorAll('meta')).filter(eligibleMeta).forEach(node => {
            document.head.appendChild(document.importNode(node, true));
        });
    };

    const syncControls = page => {
        document.querySelectorAll('collection-filters-form [name="sort_by"]').forEach(input => {
            const source = page.querySelector('collection-filters-form [name="sort_by"]');
            if (source) input.value = source.value;
        });
        const sources = page.querySelectorAll('[data-sorting]');
        document.querySelectorAll('[data-sorting]').forEach((sorting, index) => {
            const source = sources[index];
            if (!source) return;
            const label = sorting.querySelector('.label-text');
            if (label && source.querySelector('.label-text')) label.textContent = source.querySelector('.label-text').textContent;
            const active = source.querySelector('[data-sort-by-item].is-active .text');
            sorting.querySelectorAll('[data-sort-by-item]').forEach(button => {
                button.classList.toggle('is-active', !!active && button.querySelector('.text')?.dataset.href === active.dataset.href);
            });
        });
    };

    const cancel = () => {
        revision += 1;
        clearTimeout(pendingIntent);
        pendingIntent = undefined;
        controller?.abort();
        controller = undefined;
        pendingURL = undefined;
        document.body.classList.remove('has-halo-loader');
    };

    const schedule = (callback, delay = 500) => {
        cancel();
        const intentRevision = revision;
        pendingIntent = setTimeout(() => {
            pendingIntent = undefined;
            if (intentRevision === revision) callback();
        }, delay);
    };

    const navigate = async (href, render, push = true, append = false) => {
        const url = cleanURL(href);
        cancel();
        const requestRevision = revision;
        controller = new AbortController();
        const signal = controller.signal;
        pendingURL = url.href;
        if (!append) document.body.classList.add('has-halo-loader');
        const timeout = setTimeout(() => {
            if (requestRevision !== revision || signal.aborted) return;
            window.location.assign(url.href);
            cancel();
        }, 20000);
        try {
            const response = await fetch(url.href, { signal, credentials: 'same-origin', cache: 'no-store' });
            if (!response.ok || (response.headers.get('content-type') || '').split(';')[0].trim() !== 'text/html') {
                throw new Error('Collection request failed');
            }
            if (response.url && new URL(response.url).href !== url.href) throw new Error('Collection request redirected');
            const html = await response.text();
            if (requestRevision !== revision || signal.aborted) return false;
            const page = readPage(html, url);
            render(html);
            if (!append) {
                syncControls(page);
                applyHead(page);
                if (push && url.href !== window.location.href) {
                    history.pushState({ cloCollection: true }, '', url.pathname + url.search + window.location.hash);
                }
                committedURL = window.location.href;
                const Filters = customElements.get('collection-filters-form');
                if (Filters) Filters.searchParamsPrev = url.search.slice(1);
            }
            return true;
        } catch (error) {
            if (requestRevision !== revision || signal.aborted) return false;
            window.location.assign(url.href);
            return false;
        } finally {
            clearTimeout(timeout);
            if (requestRevision === revision) {
                pendingURL = undefined;
                controller = undefined;
                document.body.classList.remove('has-halo-loader');
                customElements.get('collection-filters-form')?.toggleActiveFacets(false);
            }
        }
    };

    const append = (href, render) => {
        if (controller || pendingIntent) return Promise.resolve(false);
        return navigate(href, render, false, true);
    };

    window.CLOCollectionNavigation = {
        navigate, append, cancel, schedule,
        get busy() { return !!controller || !!pendingIntent; },
        get pendingURL() { return pendingURL; }
    };
    window.addEventListener('pagehide', cancel);
    window.addEventListener('popstate', () => {
        const wasPending = !!pendingURL;
        cancel();
        if (!wasPending && window.location.href === committedURL) return;
        const Filters = customElements.get('collection-filters-form');
        if (Filters && document.querySelector('collection-filters-form')) {
            Filters.renderPage(window.location.search.slice(1), null, false);
        } else {
            const toolbar = document.querySelector('toolbar-item');
            if (toolbar) toolbar.renderPage(window.location.href, false);
            else window.location.assign(window.location.href);
        }
    });
})();
